import React, { useEffect, useState, useRef, useCallback } from "react";
import styles from "./ReportsPage.module.css";
import ReportCard from "Reporting/Components/ReportCard/ReportCard";
import ReportTableList from "Reporting/Components/ReportTableList/index";
import ReportTableListDiamond from "Reporting/Components/ReportTableListDiamond/index";
import ReportTableListQualified from "Reporting/Components/ReportTableListQualified";
import ReportTableListQualifyingGroup from "Reporting/Components/ReportTableListQualifyingGroup";
import ReportTableListADA from "Reporting/Components/ReportTableListADA";
import ReportTableListBreakout from "Reporting/Components/ReportTableListBreakout";
import ReportTableListAllAttendees from "Reporting/Components/ReportTableListAllAttendees";
import ReportTableListSessionAttendance from "Reporting/Components/ReportTableListSessionAttendance";
import ReportTableListSessionOverview from "Reporting/Components/ReportTableListSessionOverview";
import ReportTableListQuestionnaire from "Reporting/Components/ReportTableListQuestionnaire";
import ReportTableListQualifiedGroupOverview from "Reporting/Components/ReportTableListQualifiedGroupOverview";
import ReportTableListPlatinumPlus from "Reporting/Components/ReportTableListPlatinumPlus";
import ReportTableListPacing from "Reporting/Components/ReportTableListPacing";
import ReportTableListPacingDiamond from "Reporting/Components/ReportTableListPacingDiamond";

import Loader from "shared/Loader";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

import { useReportFilters } from "./useReportFilters";

const Reports = () => {
  const reportRef = useRef();
  const [reportsLength, setReportsLength] = useState(0);
  const [isReportGenerated, setIsReportGenerated] = useState(false);
  const [reportSelected, setReportSelected] = useState({});
  const [isReportCardEdited, setIsReportCardEdited] = useState(false);
  const [filters, setFilters] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [reportCardKey, setReportCardKey] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isPreparingPdf, setIsPreparingPdf] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [isSavingReport, setIsSavingReport] = useState(false);
  const [isClearingReport, setIsClearingReport] = useState(false);
  const [fetchTrigger, setFetchTrigger] = useState(0);

  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const {
    data: filterData,
    isLoading: filtersLoading,
    isError: filtersError,
    refetch: refetchFilters,
  } = useReportFilters(selectedYear);

  useEffect(() => {
    if (filterData) {
      setFilters(filterData);
    }
  }, [filterData]);

  useEffect(() => {
    // Clear saved filters whenever the page is reloaded
    localStorage.removeItem("selectedFilters");
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem("selectedFilters");
    if (saved) {
      setIsReportCardEdited(true);
      setSelectedFilters(JSON.parse(saved));
      setReportSelected(JSON.parse(saved).report);
    }
  }, []);

  useEffect(() => {
    if (selectedFilters) {
      localStorage.setItem("selectedFilters", JSON.stringify(selectedFilters));
    }
  }, [selectedFilters]);

  const handleGenerateReport = async (filterData) => {
    // await refetchFilters();
    setIsGeneratingReport(true);
    const {
      year,
      events,
      groups,
      owners,
      type,
      report,
      groupNames,
      eventNames,
      rawEvents,
      rawGroups,
      rawOwners,
      sessions,
      rawSessions,
      eventSetId,
      rawEventSets,
      asOfDate,
    } = filterData;
    setReportSelected(report);

    setSelectedFilters({
      year,
      type,
      events,
      groups,
      owners,
      groupNames,
      eventNames,
      report,
      rawEvents,
      rawGroups,
      rawOwners,
      sessions,
      rawSessions,
      eventSetId,
      rawEventSets,
      asOfDate,
    });

    setIsReportGenerated(true);
    setIsReportCardEdited(true);
    // bump fetchTrigger so child table components can refetch their data
    setFetchTrigger((t) => t + 1);
  };

  const [ticketData, setTicketsData] = useState(null);
  const [ticketsByDiamondData, setTicketsByDiamondData] = useState(null);
  const [qualifiedData, setQualifiedData] = useState(null);
  const [qualifiyingGroupData, setQualifiyingGroupData] = useState(null);
  const [qualifiedGroupOverviewData, setQualifiedGroupOverviewData] =
    useState(null);
  const [adaData, setAdaData] = useState(null);
  const [breakoutData, setBreakoutData] = useState(null);
  const [attendeesData, setAttendeesData] = useState(null);
  const [sessionAttendanceData, setSessionAttendanceData] = useState([]);
  const [sessionOverviewData, setSessionOverviewData] = useState([]);
  const [questionnaireData, setQuestionnaireData] = useState([]);
  const [platinumPlusData, setPlatinumPlusData] = useState(null);
  const [pacingData, setPacingData] = useState(null);
  const [pacingDiamondData, setPacingDiamondData] = useState(null);

  const handleAttendeesData = useCallback((data) => {
    setAttendeesData((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(data)) return prev;
      return data;
    });
  }, []);

  const handleSessionAttendanceData = useCallback((data) => {
    setSessionAttendanceData((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(data)) return prev;
      return data;
    });
  }, []);

  const handleAdaData = useCallback((data) => {
    setAdaData((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(data)) return prev;
      return data;
    });
  }, []);

  const handlePlatinumPlusData = useCallback((data) => {
    setPlatinumPlusData((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(data)) return prev;
      return data;
    });
  }, []);

  const handleQualifiyingGroupData = useCallback((data) => {
    setQualifiyingGroupData((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(data)) return prev;
      return data;
    });
  }, []);

  const handleQualifiedOverviewData = useCallback((data) => {
    setQualifiedGroupOverviewData((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(data)) return prev;
      return data;
    });
  }, []);

  const handleBreakoutData = useCallback((data) => {
    setBreakoutData((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(data)) return prev;
      return data;
    });
  }, []);

  const handleQuestionnaireData = useCallback((data) => {
    setQuestionnaireData((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(data)) return prev;
      return data;
    });
  }, []);

  const getPacingExportData = (report) => {
    const sortedRows = Array.isArray(report?.rows) ? report.rows : [];

    const headers = [
      "As Of",
      "Current Event",
      "Current Comp",
      "Current Paid",
      "Current Total",
      "Prior Event",
      "Prior Comp",
      "Prior Paid",
      "Prior Total",
      "% Change",
    ];

    const rows = sortedRows.map((row) => [
      row.asOf,
      row.isTotalRow ? "Total" : row.currentEvent,
      row.currentComp,
      row.currentPaid,
      row.currentTotal,
      row.priorEvent,
      row.priorComp,
      row.priorPaid,
      row.priorTotal,
      row.percentOfChange,
    ]);

    const rowTypes = sortedRows.map((row) =>
      row.isTotalRow ? "total" : "data"
    );

    return { headers, rows, rowTypes };
  };

  const getPacingDiamondExportData = (report) => {
    const sortedRows = Array.isArray(report?.rows) ? report.rows : [];

    const headers = [
      "As Of",
      "Diamond",
      "Current Comp",
      "Current Paid",
      "Current Total",
      "Prior Comp",
      "Prior Paid",
      "Prior Total",
      "% Change",
    ];

    const rows = sortedRows.map((row) => [
      row.asOf,
      row.isTotalRow ? "Total" : row.diamond,
      row.currentComp,
      row.currentPaid,
      row.currentTotal,
      row.priorComp,
      row.priorPaid,
      row.priorTotal,
      row.percentOfChange,
    ]);

    const rowTypes = sortedRows.map((row) =>
      row.isTotalRow ? "total" : "data"
    );

    return { headers, rows, rowTypes };
  };

  const buildReportDataForPDF = () => {
    const sections = [];
    if (reportSelected === "Ticket Purchases" && ticketData) {
      ticketData.forEach((report) => {
        const allTickets = Array.from(
          new Set(
            report.events.flatMap((event) => event.tickets.map((t) => t.ticket))
          )
        );
        const headers = ["Event", ...allTickets, "Total Tickets"];
        const rows = report.events.map((event) => {
          const ticketCounts = allTickets.map((ticketName) => {
            const t = event.tickets.find((tk) => tk.ticket === ticketName);
            return t ? t.count : 0;
          });
          const total = event.tickets.reduce(
            (sum, t) => sum + (t.isAttending ? t.count || 0 : 0),
            0
          );
          return [
            `${event.eventName} - ${event.eventDate}`,
            ...ticketCounts,
            total,
          ];
        });
        const rowTypes = rows.map(() => "data");
        if (report.events.length > 1) {
          const totalCounts = allTickets.map((ticketName) =>
            report.events.reduce((sum, event) => {
              const t = event.tickets.find((tk) => tk.ticket === ticketName);
              return sum + (t ? t.count : 0);
            }, 0)
          );
          const grandTotal = report.events.reduce(
            (sum, event) =>
              sum +
              event.tickets.reduce(
                (inner, t) => inner + (t.isAttending ? t.count || 0 : 0),
                0
              ),
            0
          );
          rows.unshift(["Total", ...totalCounts, grandTotal]);
          rowTypes.unshift("total");
        }
        sections.push({
          title: `Ticket Purchases - ${report.eventType} ${report.year}`,
          sectionTitle: `${report.eventOwner}: ${report.eventType} - ${report.year}`,
          headers,
          rows,
          rowTypes,
        });
      });
    }
    if (
      reportSelected === "Ticket Purchases By Diamond" &&
      ticketsByDiamondData
    ) {
      ticketsByDiamondData.forEach((report) => {
        if (!report) return;

        const mainTitle = `Ticket Purchases By Diamond - ${report.eventType} ${report.year}`;

        report.events.forEach((event) => {
          const eventTickets = Array.from(
            new Set(
              event.diamonds.flatMap((d) => d.tickets.map((t) => t.ticket))
            )
          );

          const headers = ["Diamond", ...eventTickets, "Total"];
          const rows = [];
          const rowTypes = [];

          event.diamonds.forEach((diamond) => {
            const ticketCounts = eventTickets.map((ticketName) => {
              const t = diamond.tickets.find((tk) => tk.ticket === ticketName);
              return t ? t.count : 0;
            });

            const total = diamond.tickets.reduce(
              (sum, t) => sum + (t.isAttending ? t.count || 0 : 0),
              0
            );
            rows.push([diamond.diamond, ...ticketCounts, total]);
            rowTypes.push("data");
          });

          const totalCounts = eventTickets.map((ticketName) =>
            event.diamonds.reduce((sum, diamond) => {
              const t = diamond.tickets.find((tk) => tk.ticket === ticketName);
              return sum + (t ? t.count : 0);
            }, 0)
          );

          const grandTotal = event.diamonds.reduce(
            (sum, diamond) =>
              sum +
              diamond.tickets.reduce(
                (inner, t) => inner + (t.isAttending ? t.count || 0 : 0),
                0
              ),
            0
          );
          rows.unshift(["Total", ...totalCounts, grandTotal]);
          rowTypes.unshift("total");

          sections.push({
            title: mainTitle,
            sectionTitle: `${event.eventName}${
              event.eventDate ? ` - ${event.eventDate}` : ""
            }`,
            headers,
            rows,
            rowTypes,
          });
        });
      });
    }
    if (reportSelected === "Qualified Attendance" && qualifiedData) {
      qualifiedData.forEach((report) => {
        if (!report) return;
        const iboRows = report.iboData || [];
        const columnOrder = [
          "Badges",
          "Other Qualifiers",
          "Breakout Events",
          "Main Event",
        ];

        // Build column metadata with group assignment
        const allColumns = [];
        columnOrder.forEach((headerName) => {
          const headerObj = report.headers.find((h) => h.header === headerName);
          if (headerObj && headerObj.columns) {
            headerObj.columns.forEach((col) => {
              const colName = col.columnName || col;
              allColumns.push({ group: headerName, column: colName });
            });
          }
        });

        // Build two-level header structure for autoTable
        const majorHeaderRow = ["IBO Data", "", "", ""];
        const subHeaderRow = ["IBO", "First Name", "Last Name", "Pin"];

        // Build merged group headers (single header cell per group with colSpan)
        columnOrder.forEach((headerName) => {
          const headerObj = report.headers.find((h) => h.header === headerName);
          if (headerObj && headerObj.columns) {
            const cols = headerObj.columns.map((c) => c.columnName || c);
            majorHeaderRow.push({ content: headerName, colSpan: cols.length });
            subHeaderRow.push(...cols);
          }
        });

        // Build data rows with cellMeta for highlighting
        const rows = [];
        const cellMeta = [];
        iboRows.forEach((ibo) => {
          const rowMeta = [];
          const dynamicCols = allColumns.map(({ group, column }) => {
            const match = ibo.headers?.find((h) => h.columnName === column);
            const value = match?.count ?? 0; // show 0 instead of blank when no count
            const display = String(value) + (match?.isOverride ? "*" : "");
            rowMeta.push({ group, column, highlight: match?.highlight });
            return display;
          });
          rows.push([
            ibo.IBO,
            ibo.firstName,
            ibo.lastName,
            ibo.pinLevel,
            ...dynamicCols,
          ]);
          cellMeta.push(rowMeta);
        });

        sections.push({
          title: report.eventName,
          sectionTitle: report.eventName,
          headers: [majorHeaderRow, subHeaderRow],
          rows,
          rowTypes: rows.map(() => "data"),
          cellMeta,
          isQualifiedAttendance: true,
          pivotGroups: columnOrder.map((headerName) => {
            const headerObj = report.headers.find(
              (h) => h.header === headerName
            );
            return {
              name: headerName,
              columns: headerObj
                ? headerObj.columns.map((c) => c.columnName || c)
                : [],
            };
          }),
        });
      });
    }
    if (
      reportSelected === "Qualified Group Attendance" &&
      qualifiyingGroupData
    ) {
      const groups = Array.from(
        new Set(qualifiyingGroupData.map((r) => r.groupName))
      );

      groups.forEach((groupName) => {
        const groupRows = qualifiyingGroupData.filter(
          (r) => r.groupName === groupName
        );

        if (groupRows.length === 0) return;

        const allColumns = Array.from(
          new Set(groupRows.flatMap((r) => r.columns?.map((c) => c.name) || []))
        );

        const headers = [
          "IBO",
          "Last Name",
          "Display Name",
          "PIN",
          "Highest PIN",
          "Email",
          "Topic ID",
          "Diamond",
          "Users",
          ...allColumns,
        ];

        const isNumericColumn = (colName) => {
          return groupRows.every((item) => {
            const match = item.columns?.find((c) => c.name === colName);
            const value = match?.value;

            if (value === null || value === undefined || value === "")
              return true;
            return !Number.isNaN(Number(value));
          });
        };

        const rows = groupRows.map((item) => {
          const dynamicCols = allColumns.map((colName) => {
            const match = item.columns?.find((c) => c.name === colName);
            return match?.value ?? "";
          });

          return [
            item.IBO,
            item.lastName,
            item.displayName,
            item.pin,
            item.hpin,
            item.email,
            item.topicsId,
            item.diamond,
            item.users,
            ...dynamicCols,
          ];
        });

        const totalRow = [
          "Total",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          ...allColumns.map((colName) => {
            if (!isNumericColumn(colName)) return "";

            return groupRows.reduce((sum, item) => {
              const match = item.columns?.find((c) => c.name === colName);
              return sum + (Number(match?.value) || 0);
            }, 0);
          }),
        ];

        const rowsWithTotal = [totalRow, ...rows];
        const rowTypes = rowsWithTotal.map((_, i) =>
          i === 0 ? "total" : "data"
        );

        sections.push({
          title: "Qualified Group Attendance",
          sectionTitle: groupName,
          headers,
          rows: rowsWithTotal,
          rowTypes,
          isQualifiedGroup: true,
        });
      });
    }
    if (reportSelected === "ADA" && adaData) {
      adaData.forEach((reportObj) => {
        const allRows =
          Array.isArray(reportObj.sortedAdaData) &&
          reportObj.sortedAdaData.length > 0
            ? reportObj.sortedAdaData
            : reportObj.adaData || [];
        if (allRows.length === 0) return;

        const headerMap = reportObj.headers.reduce((acc, h) => {
          acc[h.columnHeaderValue] = h;
          return acc;
        }, {});

        let allColumns = Array.from(
          new Set(
            allRows.flatMap(
              (item) => item.columns?.map((c) => c.columnName) || []
            )
          )
        ).sort((a, b) => {
          const colA = allRows
            .flatMap((r) => r.columns || [])
            .find((c) => c.columnName === a);
          const colB = allRows
            .flatMap((r) => r.columns || [])
            .find((c) => c.columnName === b);

          const sortA = colA?.columnHeaderValue
            ? headerMap[colA.columnHeaderValue]?.columnHeaderSort || 999
            : 999;
          const sortB = colB?.columnHeaderValue
            ? headerMap[colB.columnHeaderValue]?.columnHeaderSort || 999
            : 999;

          return sortA - sortB;
        });

        const totalRow = {
          IBO: "Total",
          lastName: "",
          displayName: "",
          pin: "",
          diamond: "",
          attendingEvent: "",
          adaName: "",
          contact: "",
          columns: allColumns.map((colName) => {
            let total = 0;
            allRows.forEach((r) => {
              const match = r.columns?.find((c) => c.columnName === colName);
              if (match && match.columnValue)
                total += Number(match.columnValue);
            });
            return { columnName: colName, columnValue: total };
          }),
        };

        const headers = [
          "IBO",
          "Last Name",
          "Display Name",
          "PIN",
          "Diamond",
          "Event",
          "ADA Name",
          "Contact",
          ...allColumns,
        ];

        const rows = allRows.map((item) => {
          const dynamicCols = allColumns.map((colName) => {
            const match = item.columns?.find((c) => c.columnName === colName);
            return match ? match.columnValue : "";
          });
          const formattedContact = item.contact
            ? item.contact.replace(/\s*\|\s*/g, "\n")
            : "";
          return [
            item.IBO,
            item.lastName,
            item.displayName,
            item.pin,
            item.diamond,
            item.attendingEvent,
            item.adaName,
            formattedContact,
            ...dynamicCols,
          ];
        });

        const formattedTotalRow = [
          totalRow.IBO,
          totalRow.lastName,
          totalRow.displayName,
          totalRow.pin,
          totalRow.diamond,
          totalRow.attendingEvent,
          totalRow.adaName,
          "", //
          ...totalRow.columns.map((c) => c.columnValue),
        ];
        const rowsWithTotal = [formattedTotalRow, ...rows];

        sections.push({
          title: "ADA Report",
          sectionTitle: reportObj.reportTitle,
          headers,
          rows: rowsWithTotal,
          rowTypes: rowsWithTotal.map((_, i) => (i === 0 ? "total" : "data")),
          isAda: true,
        });
      });
    }
    if (reportSelected === "Breakout Sessions" && breakoutData) {
      breakoutData.forEach((report) => {
        if (!report) return;
        const rowsData =
          Array.isArray(report.sortedBreakoutData) &&
          report.sortedBreakoutData.length > 0
            ? report.sortedBreakoutData
            : report.reports || [];
        const allColumns = Array.from(
          new Set(
            rowsData.flatMap(
              (row) =>
                row.columns?.map((c) => `${c.headerName} - ${c.name}`) || []
            )
          )
        );
        const headers = [
          "IBO",
          "Last Name",
          "Display Name",
          "PIN",
          "Email",
          "Topic ID",
          "Diamond",
          "Attending",
          "QGroups",
          ...allColumns,
        ];
        const rows = rowsData.map((row) => {
          const dynamicCols = allColumns.map((colIdentifier) => {
            const [headerName, name] = colIdentifier.split(" - ");
            const match = row.columns?.find(
              (c) => c.headerName === headerName && c.name === name
            );
            return match ? match.value : 0;
          });
          return [
            row.IBO,
            row.lastName,
            row.displayName,
            row.pin,
            row.email,
            row.topicsId,
            row.diamond,
            row.attending,
            (row.qGroups || []).join(", "),
            ...dynamicCols,
          ];
        });
        sections.push({
          title: report.title,
          sectionTitle: report.title,
          headers,
          rows,
          rowTypes: rows.map(() => "data"),
        });
      });
    }
    if (reportSelected === "All Attendees" && attendeesData) {
      attendeesData.forEach((report) => {
        if (!report) return;
        const rowsData = report.reports || [];
        const grouped = rowsData.reduce((acc, r) => {
          if (r.columns && typeof r.columns === "object") {
            Object.keys(r.columns).forEach((columnId) => {
              const [groupName, ticketName] = columnId.split("::");
              if (!groupName || !ticketName || ticketName === "Total") return;
              if (!acc[groupName]) acc[groupName] = new Set();
              acc[groupName].add(ticketName);
            });
            return acc;
          }
          if (!r.ticketGroup || !r.ticket) return acc;
          if (r.columns && typeof r.columns === "object") {
            Object.keys(r.columns).forEach((columnId) => {
              const [groupName, ticketName] = columnId.split("::");
              if (!groupName || !ticketName || ticketName === "Total") return;
              if (!acc[groupName]) acc[groupName] = new Set();
              acc[groupName].add(ticketName);
            });
            return acc;
          }
          if (!r.ticketGroup || !r.ticket) return acc;
          if (!acc[r.ticketGroup]) acc[r.ticketGroup] = new Set();
          acc[r.ticketGroup].add(r.ticket);
          return acc;
        }, {});
        const sortedGroups = Object.keys(grouped).sort((a, b) =>
          a.localeCompare(b)
        );

        // Build two-level headers (major group row + sub ticket row)
        const identityColumns = [
          "IBO",
          "A Last Name",
          "A First Name",
          "B Last Name",
          "B First Name",
          "PIN",
          "Diamond",
          "Attending",
          "Email",
          "Phone",
          "Topics",
        ];
        const majorHeaderRow = [
          { content: "IBO Data", colSpan: identityColumns.length },
        ];
        const subHeaderRow = [...identityColumns];

        const ticketColumnsFlat = [];
        const pivotGroups = [];
        sortedGroups.forEach((groupName) => {
          const tickets = Array.from(grouped[groupName]).sort();
          const ticketNames = tickets.map((t) => t);
          const groupColCount =
            ticketNames.length + (groupName === "Attending Tickets" ? 1 : 0);
          // Single merged header cell for the ticket group
          majorHeaderRow.push({ content: groupName, colSpan: groupColCount });
          ticketNames.forEach((t) => {
            subHeaderRow.push(t);
            ticketColumnsFlat.push({ group: groupName, ticket: t });
          });
          if (groupName === "Attending Tickets") {
            subHeaderRow.push("Total");
            ticketColumnsFlat.push({ group: groupName, ticket: "Total" });
          }
          pivotGroups.push({
            name: groupName,
            columns: [
              ...ticketNames,
              ...(groupName === "Attending Tickets" ? ["Total"] : []),
            ],
          });
        });

        const rows = rowsData.map((row) => {
          const base = [
            row.IBO,
            row.iboALastName,
            row.iboAFirstName,
            row.iboBLastName,
            row.iboBFirstName,
            row.pin,
            row.diamond,
            row.attending,
            row.email,
            row.phone,
            row.topicsId,
          ];

          const dynamicCols = [];
          sortedGroups.forEach((groupName) => {
            const tickets = Array.from(grouped[groupName]).sort();
            const usingOrganizedColumns =
              row.columns && typeof row.columns === "object";

            let groupTotal = 0;

            tickets.forEach((ticketName) => {
              const value = usingOrganizedColumns
                ? Number(row.columns?.[`${groupName}::${ticketName}`]) || 0
                : row.ticketGroup === groupName && row.ticket === ticketName
                ? Number(row.purchased) || 0
                : 0;

              if (groupName === "Attending Tickets") groupTotal += value;

              dynamicCols.push(value);
            });

            if (groupName === "Attending Tickets") {
              const attendingTotal = usingOrganizedColumns
                ? Number(row.attendingTotal) || 0
                : groupTotal;

              dynamicCols.push(attendingTotal);
            }
          });

          return [...base, ...dynamicCols];
        });

        const totalDynamic = new Array(rows[0]?.length - 11).fill(0);

        rows.forEach((r) => {
          for (let i = 11; i < r.length; i++) {
            totalDynamic[i - 11] += Number(r[i]) || 0;
          }
        });

        const totalRow = [
          "Total",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          ...totalDynamic,
        ];

        const rowsWithTotal = [totalRow, ...rows];
        const rowTypes = rowsWithTotal.map((_, i) =>
          i === 0 ? "total" : "data"
        );

        sections.push({
          title: "All Attendees",
          sectionTitle: report.title,
          headers: [majorHeaderRow, subHeaderRow],
          rows: rowsWithTotal,
          rowTypes,
          isAllAttendeesGrouped: true,
          pivotGroups,
        });
      });
    }
    if (reportSelected === "Session Attendance" && sessionAttendanceData) {
      sessionAttendanceData.forEach((report) => {
        if (!report) return;

        const headers = ["IBO", "Last Name", "First Name", "Ticket", "Diamond"];

        const rows = report.reports.map((attendee) => [
          attendee.IBO,
          attendee.lastName,
          attendee.firstName,
          attendee.ticket,
          attendee.diamond,
        ]);

        const totalAttendees = report.reports.length;

        sections.push({
          title: "Session Attendance",
          sectionTitle: report.title,
          headers,
          rows,
          rowTypes: rows.map(() => "data"),
          isSessionAttendance: true,
          subTitle: `Total Attendees: ${String(totalAttendees)}`,
        });
      });
    }
    if (reportSelected === "Session Overview" && sessionOverviewData) {
      const mainTitle = sessionOverviewData.title || "Session Overview";
      const headers = ["Session", "Attending", "Capacity", "PCT"];
      const rows = [];
      const rowTypes = [];

      sessionOverviewData.rows.forEach((session) => {
        rows.push([
          session["Session Title"],
          session.Attending,
          session["Room Capacity"],
          session.PCT,
        ]);
        rowTypes.push("data");
      });

      sections.push({
        title: mainTitle,
        headers,
        rows,
        rowTypes,
      });
    }
    if (reportSelected === "Questionnaire" && questionnaireData?.length) {
      questionnaireData.forEach((report) => {
        if (!report?.reports?.length) return;

        const baseHeaders = ["IBO", "Name", "Attending"];
        const allQuestions = Array.from(
          new Set(report.reports.map((r) => r.questionColumn))
        );

        const headers = [...baseHeaders, ...allQuestions];

        const rows = report.reports.map((r) => {
          const baseRow = [r.IBO, r.name, r.attending];
          const dynamicCols = allQuestions.map((q) =>
            r.questionColumn === q ? r.responseValue : ""
          );
          return [...baseRow, ...dynamicCols];
        });

        sections.push({
          title: "Questionnaire",
          sectionTitle: report.title,
          headers,
          rows,
          rowTypes: rows.map(() => "data"),
          totals: report.totals,
          isQuestionnaire: true,
        });
      });
    }
    if (
      reportSelected === "Qualified Group Overview" &&
      qualifiedGroupOverviewData
    ) {
      qualifiedGroupOverviewData.forEach((report) => {
        if (!report) return;

        const sortedColumnGroups = Array.isArray(report.columnGroups)
          ? [...report.columnGroups].sort(
              (a, b) => (a.columnHeaderSort || 0) - (b.columnHeaderSort || 0)
            )
          : [];

        const flattenedColumns = sortedColumnGroups.flatMap((group) =>
          (group.columns || [])
            .slice()
            .sort((a, b) => (a.columnSort || 0) - (b.columnSort || 0))
            .map((column) => ({
              groupName: group.columnHeader,
              columnName: column.columnName,
            }))
        );

        const majorHeaderRow = [{ content: "Qualified Group", rowSpan: 2 }];

        sortedColumnGroups.forEach((group) => {
          const sortedColumns = (group.columns || [])
            .slice()
            .sort((a, b) => (a.columnSort || 0) - (b.columnSort || 0));

          majorHeaderRow.push({
            content: group.columnHeader,
            colSpan: sortedColumns.length,
          });
        });

        const subHeaderRow = flattenedColumns.map((col) => col.columnName);

        const sortedRows = Array.isArray(report.rows)
          ? [...report.rows].sort(
              (a, b) => Number(a?.rowSort || 0) - Number(b?.rowSort || 0)
            )
          : [];

        const getNumericValue = (row, groupName, columnName) => {
          const value = row?.values?.[groupName]?.[columnName];
          return Number(value) || 0;
        };

        const getAttendingTotal = (row, report) => {
          const attendingGroup = (report.columnGroups || []).find(
            (group) => group.columnHeader === "Attending"
          );

          if (!attendingGroup) return 0;

          return (attendingGroup.columns || []).reduce((sum, column) => {
            return sum + getNumericValue(row, "Attending", column.columnName);
          }, 0);
        };

        const rows = sortedRows.map((row) => {
          const dynamicValues = flattenedColumns.map((col) => {
            let value;

            if (
              col.groupName === "Attending Total" &&
              col.columnName === "Tickets"
            ) {
              value = getAttendingTotal(row, report);
            } else {
              value = row?.values?.[col.groupName]?.[col.columnName];
            }

            if (value === null || value === undefined || value === "") {
              return "";
            }

            const numericValue = Number(value);
            if (!Number.isNaN(numericValue) && numericValue === 0) {
              return "-";
            }

            return value;
          });

          return [row.qualifiedGroup, ...dynamicValues];
        });

        const totalValues = {};
        sortedColumnGroups.forEach((group) => {
          totalValues[group.columnHeader] = {};

          (group.columns || []).forEach((column) => {
            const total = sortedRows.reduce((sum, row) => {
              if (
                group.columnHeader === "Attending Total" &&
                column.columnName === "Tickets"
              ) {
                return sum + getAttendingTotal(row, report);
              }

              return (
                sum +
                getNumericValue(row, group.columnHeader, column.columnName)
              );
            }, 0);

            totalValues[group.columnHeader][column.columnName] = total;
          });
        });

        const totalRow = [
          "Total",
          ...flattenedColumns.map(
            (col) => totalValues[col.groupName]?.[col.columnName] ?? 0
          ),
        ];

        const rowsWithTotal = [totalRow, ...rows];
        const rowTypes = rowsWithTotal.map((_, index) =>
          index === 0 ? "total" : "data"
        );

        sections.push({
          title: "Qualified Group Overview",
          sectionTitle: report.reportTitle,
          headers: [majorHeaderRow, subHeaderRow],
          rows: rowsWithTotal,
          rowTypes,
          isQualifiedGroupOverview: true,
          pivotGroups: sortedColumnGroups.map((group) => ({
            name: group.columnHeader,
            columns: (group.columns || [])
              .slice()
              .sort((a, b) => (a.columnSort || 0) - (b.columnSort || 0))
              .map((column) => column.columnName),
          })),
        });
      });
    }
    if (reportSelected === "Platinum Plus" && platinumPlusData) {
      const reports = Array.isArray(platinumPlusData) ? platinumPlusData : [platinumPlusData];
      
      reports.forEach((report) => {
        const selectedColumnIds = report.selectedColumns || [];

        const sortedColumnGroups = Array.isArray(report.columnGroups)
          ? [...report.columnGroups].sort(
              (a, b) => (Number(a.sort) || 0) - (Number(b.sort) || 0)
            )
          : [];

        const filteredColumnGroups = sortedColumnGroups
          .map((group) => {
            const columns = (group.columns || [])
              .slice()
              .sort((a, b) => {
                const sortDiff = (Number(a.sort) || 0) - (Number(b.sort) || 0);
                if (sortDiff !== 0) return sortDiff;
                return (a.name || "").localeCompare(b.name || "");
              })
              .filter((column) =>
                selectedColumnIds.includes(`${group.header}::${column.name}`)
              );

            return {
              ...group,
              columns,
            };
          })
          .filter((group) => group.columns.length > 0);

        const flattenedColumns = filteredColumnGroups.flatMap((group) =>
          group.columns.map((column) => ({
            groupName: group.header,
            columnName: column.name,
          }))
        );

        const identityColumns = [
          "IBO",
          "Last Name",
          "First Name",
          "Diamond",
          "Pin Level",
          "Users",
          "In-Person",
          "Virtual",
        ];

        const majorHeaderRow = [
          { content: "IBO Data", colSpan: identityColumns.length },
        ];
        const subHeaderRow = [...identityColumns];

        filteredColumnGroups.forEach((group) => {
          majorHeaderRow.push({
            content: group.header,
            colSpan: group.columns.length,
          });

          group.columns.forEach((column) => {
            subHeaderRow.push(column.name);
          });
        });

        const rowsData = Array.isArray(report.rows) ? report.rows : [];

        const getNumericValue = (row, groupName, columnName) =>
          Number(row?.values?.[groupName]?.[columnName] || 0);

        const rows = rowsData.map((row) => [
          row.ibo,
          row.lastName,
          row.firstName,
          row.diamond,
          row.pin,
          row.users,
          row.inPerson,
          row.virtual,
          ...flattenedColumns.map((col) =>
            getNumericValue(row, col.groupName, col.columnName)
          ),
        ]);

        const totalRow = [
          "Totals:",
          "",
          "",
          "",
          "",
          "",
          "",
          "",
          ...flattenedColumns.map((col) =>
            rowsData.reduce(
              (sum, row) =>
                sum + getNumericValue(row, col.groupName, col.columnName),
              0
            )
          ),
        ];

        const rowsWithTotal = [totalRow, ...rows];
        const rowTypes = rowsWithTotal.map((_, index) =>
          index === 0 ? "total" : "data"
        );

        sections.push({
          title: "Platinum Plus",
          sectionTitle: report.reportTitle,
          headers: [majorHeaderRow, subHeaderRow],
          rows: rowsWithTotal,
          rowTypes,
          isPlatinumPlus: true,
          pivotGroups: filteredColumnGroups.map((group) => ({
            name: group.header,
            columns: group.columns.map((column) => column.name),
          })),
        });
      });
    }
    if (reportSelected === "Pacing Report" && pacingData) {
      const { headers, rows, rowTypes } = getPacingExportData(pacingData);

      if (rows.length > 0) {
        sections.push({
          title: pacingData.reportTitle || "Pacing Report",
          sectionTitle: pacingData.reportTitle || "Pacing Report",
          headers,
          rows,
          rowTypes,
          isPacing: true,
        });
      }
    }
    if (reportSelected === "Pacing Report with Diamond" && pacingDiamondData) {
      pacingDiamondData.forEach((report) => {
        const { headers, rows, rowTypes } = getPacingDiamondExportData(report);

        if (rows.length === 0) return;

        const showEventInfo =
          report.currentEvent !== "- All Events -" &&
          report.priorEvent !== "- All Events -";

        sections.push({
          title: report.reportTitle || "Pacing Report with Diamond",
          sectionTitle: report.reportTitle || "Pacing Report with Diamond",
          subTitle: showEventInfo
            ? `Current Event: ${report.currentEvent} | Prior Event: ${report.priorEvent}`
            : null,
          headers,
          rows,
          rowTypes,
          isPacingDiamond: true,
        });
      });
    }
    return sections;
  };

  /*Generate a PDF of the current report using jsPDF + autoTable.*/
  const handleGeneratePDF = async () => {
    if (!reportRef.current) return;
    setIsLoading(true);
    setIsPreparingPdf(true);

    try {
      // Dev-only diagnostic: log any undefined/null datasets when attempting PDF generation
      if (
        typeof window !== "undefined" &&
        /localhost|127\.0\.0\.1/.test(window.location.hostname)
      ) {
        const debugSources = {
          reportSelected,
          ticketData,
          ticketsByDiamondData,
          qualifiedData,
          qualifiyingGroupData,
          qualifiedGroupOverviewData,
          adaData,
          breakoutData,
          attendeesData,
          sessionAttendanceData,
          sessionOverviewData,
          questionnaireData,
          platinumPlusData,
          pacingData,
          pacingDiamondData,
        };
        const missing = Object.entries(debugSources).filter(
          ([, v]) => v == null
        );
        if (missing.length) {
          // Consolidated console output for quick scanning
          console.warn(
            "[PDF DEBUG] Missing/undefined data sources:",
            missing.map((m) => m[0])
          );
        }
      }

      const dataModel = buildReportDataForPDF();
      if (!dataModel || dataModel.length === 0) {
        alert(
          "No data available to generate PDF. Please ensure a report is generated first."
        );
        setIsLoading(false);
        setIsPreparingPdf(false);
        return;
      }

      const sections = dataModel; // already an array
      const firstSection = sections[0];

      // Determine orientation based on number of columns in first section
      const headerRowForOrientation = Array.isArray(firstSection.headers[0])
        ? firstSection.headers[1]
        : firstSection.headers;
      const numCols = headerRowForOrientation.length;
      // Force landscape for Qualified Attendance or reports with many columns
      const orientation =
        firstSection.isQualifiedAttendance ||
        firstSection.isQualifiedGroupOverview ||
        firstSection.isPlatinumPlus ||
        numCols > 8
          ? "landscape"
          : "portrait";

      const pdf = new jsPDF({
        orientation: orientation,
        unit: "pt",
        format: "a4",
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 30; // Reduced margin for more space

      // Title
      let mainTitle = firstSection.title || "Report";
      if (
        firstSection.isQualifiedAttendance &&
        qualifiedData &&
        qualifiedData.length > 0
      ) {
        const eventName = qualifiedData[0]?.eventName || mainTitle;
        mainTitle = `${eventName} - Qualified Attendance`;
      }

      const showMainTitle = !firstSection.isPacingDiamond;
      if (showMainTitle) {
        pdf.setFontSize(16);
        pdf.setFont(undefined, "bold");
        pdf.text(mainTitle, margin, margin);
      }

      // Add report metadata if available
      let startY = showMainTitle ? margin + 25 : margin;
      if (selectedFilters) {
        pdf.setFontSize(9);
        pdf.setFont(undefined, "normal");
        const metaInfo = [];
        if (selectedFilters.year)
          metaInfo.push(`Year: ${selectedFilters.year}`);
        if (selectedFilters.type)
          metaInfo.push(`Type: ${selectedFilters.type}`);
        if (selectedFilters.asOfDate)
          metaInfo.push(`As Of Date: ${selectedFilters.asOfDate}`);
        if (
          selectedFilters.eventNames &&
          selectedFilters.eventNames.length > 0
        ) {
          const events = selectedFilters.eventNames
            .filter((e) => e && e !== "All")
            .join(", ");
          if (events) metaInfo.push(`Events: ${events}`);
        }
        if (selectedFilters.sessions && selectedFilters.sessions.length > 0) {
          const sessions = selectedFilters.sessions
            .filter((e) => e && e !== "All")
            .join(", ");
          if (sessions) metaInfo.push(`Sessions: ${sessions}`);
        }
        if (metaInfo.length > 0) {
          pdf.text(metaInfo.join(" | "), margin, startY);
          startY += 20;
        }
      }

      // Process each section (for multiple tabled reports)
      sections.forEach((section, sectionIndex) => {
        if (sectionIndex > 0) {
          // Add page break between sections or continue on same page with spacing
          const finalY = pdf.lastAutoTable ? pdf.lastAutoTable.finalY : startY;
          if (finalY + 100 > pageHeight - margin) {
            pdf.addPage();
            startY = margin + 25;
          } else {
            startY = finalY + 30;
          }
        }

        //Display totals section
        if (section.totals && section.totals.length > 0) {
          let totalY = startY;
          pdf.setFontSize(10);
          pdf.setFont(undefined, "bold");

          section.totals.forEach((t) => {
            pdf.text(`${t.questionnaire}:`, margin, totalY);
            totalY += 14;

            t.questions.forEach((q) => {
              pdf.setFont(undefined, "normal");
              pdf.text(`- ${q.question}`, margin + 8, totalY);
              totalY += 12;

              q.answers.forEach((a) => {
                pdf.text(`${a.answer}: ${a.total}`, margin + 16, totalY);
                totalY += 12;
              });
            });

            totalY += 12;
          });

          startY = totalY;
        }

        // Section title (event name for Diamond report, etc.)
        if (
          section.sectionTitle &&
          (sections.length > 1 ||
            section.isAllAttendeesGrouped ||
            section.isSessionAttendance ||
            section.isQualifiedGroup ||
            section.isQuestionnaire ||
            section.isAda ||
            section.isPacingDiamond)
        ) {
          pdf.setFontSize(12);
          pdf.setFont(undefined, "bold");
          pdf.setTextColor(41, 128, 185);
          let sectionTitle = section.sectionTitle;
          if (
            section.isQualifiedAttendance &&
            qualifiedData &&
            qualifiedData.length > 0
          ) {
            const eventName =
              qualifiedData[0]?.eventName || section.sectionTitle;
            sectionTitle = `${eventName} - Qualified Attendance`;
          }
          pdf.text(sectionTitle, margin, startY);
          pdf.setTextColor(0, 0, 0);
          startY += 20;
        }

        if (section.subTitle) {
          pdf.setFontSize(9);
          pdf.setFont(undefined, "bold");
          pdf.text(section.subTitle, margin, startY);
          startY += 10;
        }

        // Calculate column widths dynamically based on content length
        const headerRowForCounting = Array.isArray(section.headers[0])
          ? section.headers[1]
          : section.headers;

        const numColumns =
          section.rows?.[0]?.length ||
          (Array.isArray(section.headers[0])
            ? Math.max(...section.headers.map((row) => row.length))
            : section.headers.length);
        const availableWidth = pageWidth - margin * 2;

        // Adjust font size aggressively based on number of columns to fit all data
        let fontSize = 8;
        let cellPadding = 2;
        let headerFontSize = 8;

        if (numColumns > 40) {
          fontSize = 4;
          headerFontSize = 4.5;
          cellPadding = 1;
        } else if (numColumns > 30) {
          fontSize = 5;
          headerFontSize = 5.5;
          cellPadding = 1;
        } else if (numColumns > 25) {
          fontSize = 5.5;
          headerFontSize = 6;
          cellPadding = 1.5;
        } else if (numColumns > 20) {
          fontSize = 6;
          headerFontSize = 6.5;
          cellPadding = 1.5;
        } else if (numColumns > 15) {
          fontSize = 7;
          headerFontSize = 7.5;
          cellPadding = 2;
        }

        let columnStyles = {};
        if (section.isQualifiedAttendance) {
          const identityWidths = [50, 65, 65, 50];
          const sumIdentity = identityWidths.reduce((a, b) => a + b, 0);
          const countCols = numColumns - 4;
          const remainingWidth = availableWidth - sumIdentity;
          const perCountWidth = remainingWidth / Math.max(countCols, 1);
          identityWidths.forEach((w, i) => {
            columnStyles[i] = {
              cellWidth: w,
              overflow: "linebreak",
              halign: "left",
              fontSize: fontSize - 1,
            };
          });
          for (let i = 4; i < numColumns; i++) {
            columnStyles[i] = {
              cellWidth: perCountWidth,
              overflow: "linebreak",
              halign: "center",
            };
          }
        } else if (section.isQualifiedGroupOverview) {
          const baseWidths = {
            0: 170, // Qualified Group
          };

          const dynamicStart = 1;
          const baseSum = Object.values(baseWidths).reduce((a, b) => a + b, 0);
          const dynCount = Math.max(numColumns - dynamicStart, 0);
          const remainingWidth = Math.max(availableWidth - baseSum, 100);
          const perDynWidth = Math.max(
            remainingWidth / Math.max(dynCount, 1),
            42
          );

          Object.entries(baseWidths).forEach(([idxStr, width]) => {
            const i = Number(idxStr);
            columnStyles[i] = {
              cellWidth: width,
              overflow: "linebreak",
              halign: "left",
            };
          });

          for (let i = dynamicStart; i < numColumns; i++) {
            columnStyles[i] = {
              cellWidth: perDynWidth,
              overflow: "linebreak",
              halign: "center",
            };
          }
        } else if (section.isQualifiedGroup) {
          const baseWidths = {
            0: 48, // IBO
            1: 68, // Last Name
            2: 95, // Display Name
            3: 55, // PIN
            4: 80, // Highest PIN
            5: 120, // Email
            6: 48, // Topic ID
            7: 75, // Diamond
            8: 42, // Users
          };
          const dynamicStart = 9;
          const baseSum = Object.values(baseWidths).reduce((a, b) => a + b, 0);
          const dynCount = Math.max(numColumns - dynamicStart, 0);
          const remainingWidth = Math.max(availableWidth - baseSum, 100);
          const perDynWidth = Math.max(
            remainingWidth / Math.max(dynCount, 1),
            28
          );

          Object.entries(baseWidths).forEach(([idxStr, width]) => {
            const i = Number(idxStr);
            columnStyles[i] = {
              cellWidth: width,
              overflow: "linebreak",
              halign: i <= 2 ? "left" : "center",
            };
          });
          for (let i = dynamicStart; i < numColumns; i++) {
            columnStyles[i] = {
              cellWidth: perDynWidth,
              overflow: "linebreak",
              halign: "center",
            };
          }
        } else if (section.isPlatinumPlus) {
          const baseWidths = {
            0: 48, // IBO
            1: 70, // Last Name
            2: 70, // First Name
            3: 80, // Diamond
            4: 55, // Pin Level
            5: 42, // Users
            6: 50, // In-Person
            7: 50, // Virtual
          };

          const dynamicStart = 8;
          const baseSum = Object.values(baseWidths).reduce((a, b) => a + b, 0);
          const dynCount = Math.max(numColumns - dynamicStart, 0);
          const remainingWidth = Math.max(availableWidth - baseSum, 100);
          const perDynWidth = Math.max(
            remainingWidth / Math.max(dynCount, 1),
            34
          );

          Object.entries(baseWidths).forEach(([idxStr, width]) => {
            const i = Number(idxStr);
            columnStyles[i] = {
              cellWidth: width,
              overflow: "linebreak",
              halign: i <= 3 ? "left" : "center",
            };
          });

          for (let i = dynamicStart; i < numColumns; i++) {
            columnStyles[i] = {
              cellWidth: perDynWidth,
              overflow: "linebreak",
              halign: "center",
            };
          }
        } else if (section.isPacing) {
          const widths = [110, 150, 42, 42, 42, 150, 42, 42, 42, 42];

          widths.forEach((width, i) => {
            columnStyles[i] = {
              cellWidth: width,
              overflow: "linebreak",
              halign: i === 0 || i === 1 || i === 5 ? "left" : "right",
            };
          });
        } else if (section.isPacingDiamond) {
          const widths = [120, 150, 55, 55, 55, 55, 55, 55, 55];

          widths.forEach((width, i) => {
            columnStyles[i] = {
              cellWidth: width,
              overflow: "linebreak",
              halign: i === 0 || i === 1 ? "left" : "right",
            };
          });
        } else if (
          headerRowForCounting[0] === "IBO" &&
          headerRowForCounting[1] === "Last Name" &&
          headerRowForCounting[2] === "Display Name" &&
          headerRowForCounting[3] === "PIN" &&
          headerRowForCounting[4] === "Diamond" &&
          headerRowForCounting[5] === "Event" &&
          headerRowForCounting[6] === "ADA Name" &&
          headerRowForCounting[7] === "Contact"
        ) {
          const baseWidths = {
            0: 42, // IBO
            1: 65, // Last Name
            2: 80, // Display Name
            3: 35, // PIN
            4: 50, // Diamond
            5: 70, // Event
            6: 65, // ADA Name
            7: 180, // Contact
          };
          const dynamicStart = 8;
          const baseSum = Object.values(baseWidths).reduce((a, b) => a + b, 0);
          const dynCount = Math.max(numColumns - dynamicStart, 0);
          const remainingWidth = Math.max(availableWidth - baseSum, 50);
          const perDynWidth = Math.max(
            remainingWidth / Math.max(dynCount, 1),
            30
          );

          Object.entries(baseWidths).forEach(([idxStr, width]) => {
            const i = Number(idxStr);
            columnStyles[i] = {
              cellWidth: width,
              overflow: "linebreak",
              halign: i <= 2 ? "left" : "center",
            };
          });
          for (let i = dynamicStart; i < numColumns; i++) {
            columnStyles[i] = {
              cellWidth: perDynWidth,
              overflow: "linebreak",
              halign: "center",
            };
          }
        } else if (
          headerRowForCounting[0] === "IBO" &&
          headerRowForCounting[1] === "Last Name" &&
          headerRowForCounting[2] === "Display Name" &&
          headerRowForCounting[3] === "PIN"
        ) {
          const baseWidths = {
            0: 48, // IBO
            1: 68, // Last Name
            2: 95, // Display Name
            3: 55, // PIN
            4: 145, // Email
            5: 48, // Topic ID
            6: 75, // Diamond
            7: 42, // Users
          };
          const dynamicStart = 8;
          const baseSum = Object.values(baseWidths).reduce((a, b) => a + b, 0);
          const dynCount = Math.max(numColumns - dynamicStart, 0);
          const remainingWidth = Math.max(availableWidth - baseSum, 100);
          const perDynWidth = Math.max(
            remainingWidth / Math.max(dynCount, 1),
            28
          );

          Object.entries(baseWidths).forEach(([idxStr, width]) => {
            const i = Number(idxStr);
            columnStyles[i] = {
              cellWidth: width,
              overflow: "linebreak",
              halign: i <= 2 ? "left" : "center",
            };
          });
          for (let i = dynamicStart; i < numColumns; i++) {
            columnStyles[i] = {
              cellWidth: perDynWidth,
              overflow: "linebreak",
              halign: "center",
            };
          }
        } else if (
          // Detect All Attendees (sub-header row begins with identity columns including Topics at index 10)
          headerRowForCounting[0] === "IBO" &&
          headerRowForCounting[1] === "A Last Name" &&
          headerRowForCounting[10] === "Topics"
        ) {
          // Wide layout for All Attendees: keep generous pivot widths while restoring fuller identity/base widths.
          // Target: Base section can use up to ~55% of width; dynamic (tickets) get the remaining.
          let baseWidths = {
            0: 50, // IBO
            1: 70, // A Last Name (more natural)
            2: 65, // A First Name
            3: 70, // B Last Name
            4: 65, // B First Name
            5: 40, // PIN
            6: 60, // Diamond
            7: 60, // Attending
            8: 140, // Email
            9: 75, // Phone
            10: 60, // Topics
          };
          const dynamicStart = 11;
          const dynCount = Math.max(numColumns - dynamicStart, 0);
          let baseSum = Object.values(baseWidths).reduce((a, b) => a + b, 0);
          const availableForAll = availableWidth;

          // Allow base columns up to 55% of width to avoid looking overly compressed
          const maxBasePct = 0.55;
          if (baseSum > availableForAll * maxBasePct) {
            const scale = (availableForAll * maxBasePct) / baseSum;
            Object.keys(baseWidths).forEach((k) => {
              baseWidths[k] = Math.max(Math.floor(baseWidths[k] * scale), 34);
            });
            baseSum = Object.values(baseWidths).reduce((a, b) => a + b, 0);
          }

          // Desired width per dynamic column aiming for readability
          const desiredDynWidth = 65; // keep generous pivot width
          let remainingWidth = Math.max(availableForAll - baseSum, 0);
          let perDynWidth = dynCount ? remainingWidth / dynCount : 0;

          // If dynamic width too small (<50), attempt shrinking base (down to min 32) to boost pivots
          if (dynCount && perDynWidth < 50) {
            let shrinkIterations = 0;
            while (perDynWidth < 50 && shrinkIterations < 5) {
              let adjusted = false;
              Object.keys(baseWidths).forEach((k) => {
                if (baseWidths[k] > 32) {
                  baseWidths[k] = Math.max(baseWidths[k] - 4, 32);
                  adjusted = true;
                }
              });
              if (!adjusted) break;
              baseSum = Object.values(baseWidths).reduce((a, b) => a + b, 0);
              remainingWidth = Math.max(availableForAll - baseSum, 0);
              perDynWidth = remainingWidth / dynCount;
              shrinkIterations++;
            }
          }

          // Cap dynamic width if excessive, but keep minimum high for readability
          perDynWidth = dynCount
            ? Math.max(Math.min(perDynWidth, desiredDynWidth), 50)
            : 0;

          // Final safety: if still overflow, proportionally reduce dynamic widths (never below 40)
          const totalProjected = baseSum + perDynWidth * dynCount;
          if (totalProjected > availableForAll && dynCount > 0) {
            const overflow = totalProjected - availableForAll;
            const adjustedPer = Math.max(
              (perDynWidth * dynCount - overflow) / dynCount,
              40
            );
            perDynWidth = adjustedPer;
          }

          // If there's remaining slack after allocating desired pivot width, distribute it back into base columns proportionally
          const finalUsed = baseSum + perDynWidth * dynCount;
          if (finalUsed < availableForAll && baseSum > 0) {
            const slack = availableForAll - finalUsed;
            const weightSum = Object.values(baseWidths).reduce(
              (a, b) => a + b,
              0
            );
            Object.keys(baseWidths).forEach((k) => {
              const add = (baseWidths[k] / weightSum) * slack;
              baseWidths[k] += add;
            });
          }

          // Recompute baseSum after slack distribution for accurate final widths
          baseSum = Object.values(baseWidths).reduce((a, b) => a + b, 0);

          Object.entries(baseWidths).forEach(([idxStr, width]) => {
            const i = Number(idxStr);
            columnStyles[i] = {
              cellWidth: width,
              overflow: "linebreak",
              halign: i < 8 ? "left" : "center", // align identity columns left
            };
          });
          for (let i = dynamicStart; i < numColumns; i++) {
            columnStyles[i] = {
              cellWidth: perDynWidth,
              overflow: "linebreak",
              halign: "center",
            };
          }
        } else {
          const colWeights = headerRowForCounting.map((h, idx) => {
            const headerLen = String(h).length;
            const maxCellLen = section.rows.reduce((m, r) => {
              const cell = r[idx];
              return Math.max(m, String(cell ?? "").length);
            }, 0);

            let weight;
            if (maxCellLen <= 3 && headerLen > 20) {
              // Use 40% of header length, capped at 25 for very long headers
              weight = Math.min(headerLen * 0.4, 25);
            } else if (maxCellLen > headerLen) {
              // Content is longer than header: prioritize content
              weight = maxCellLen + 2;
            } else {
              // Balanced approach: weighted average favoring header slightly
              weight = headerLen * 0.7 + maxCellLen * 0.3;
            }

            return Math.max(weight, 8); // Minimum weight to prevent too-narrow columns
          });
          const weightSum = colWeights.reduce((a, b) => a + b, 0) || 1;
          colWeights.forEach((w, i) => {
            const width = (w / weightSum) * availableWidth;
            columnStyles[i] = { cellWidth: width, overflow: "linebreak" };
            if (i < 4) columnStyles[i].halign = "left";
            else columnStyles[i].halign = "center";
          });
        }

        // Generate the table with pivot column & total row support
        autoTable(pdf, {
          head: Array.isArray(section.headers[0])
            ? section.headers
            : [section.headers],
          body: section.rows,
          startY: startY,
          margin: { left: margin, right: margin },
          tableWidth: "wrap",
          styles: {
            fontSize: fontSize,
            cellPadding: cellPadding,
            overflow: "linebreak",
            valign: "middle",
            halign: "center",
            minCellHeight: section.isQualifiedAttendance ? 10 : 12,
            lineWidth: 0.1,
          },
          headStyles: {
            fillColor: [41, 128, 185],
            textColor: 255,
            fontStyle: "bold",
            fontSize: headerFontSize,
            halign: "center",
            valign: "middle",
            minCellHeight: 15,
            cellPadding: cellPadding,
          },
          alternateRowStyles: {
            fillColor: [245, 245, 245],
          },
          columnStyles: columnStyles,
          didDrawPage: (data) => {
            // Footer with page number
            pdf.setFontSize(8);
            pdf.setTextColor(128);
            const pageStr = `Page ${data.pageNumber}`;
            const textWidth = pdf.getTextWidth(pageStr);
            pdf.text(pageStr, pageWidth - margin - textWidth, pageHeight - 20);

            // Add title on each page
            if (data.pageNumber > 1) {
              pdf.setFontSize(10);
              pdf.setTextColor(100);
              let pageTitle = section.sectionTitle || section.title || "Report";
              if (
                section.isQualifiedAttendance &&
                qualifiedData &&
                qualifiedData.length > 0
              ) {
                const eventName = qualifiedData[0]?.eventName || pageTitle;
                pageTitle = `${eventName} - Qualified Attendance`;
              }
              pdf.text(pageTitle, margin, margin - 10);
            }
          },
          didParseCell: (data) => {
            // Qualified Attendance multi-level header styling
            if (
              (section.isQualifiedAttendance ||
                section.isAllAttendeesGrouped ||
                section.isPlatinumPlus) &&
              data.section === "head"
            ) {
              if (data.row.index === 0) {
                // Support merged header cells (objects with colSpan)
                let cellText = "";
                if (Array.isArray(data.cell.text)) {
                  cellText = data.cell.text.join(" ");
                } else if (data.cell.text) {
                  cellText = String(data.cell.text);
                } else if (data.cell.raw && data.cell.raw.content) {
                  cellText = String(data.cell.raw.content);
                } else if (data.cell.raw) {
                  cellText = String(data.cell.raw);
                }
                // Color palette for group headers (cycled if more groups)
                const palette = [
                  [52, 152, 219], // blue
                  [46, 204, 113], // green
                  [155, 89, 182], // purple
                  [230, 126, 34], // orange
                  [241, 196, 15], // yellow
                  [52, 73, 94], // dark slate
                ];
                if (cellText === "IBO Data") {
                  data.cell.styles.fillColor = [65, 83, 100];
                  data.cell.styles.textColor = 255;
                } else if (cellText) {
                  // Map group name deterministically to a palette index
                  let hash = 0;
                  for (let i = 0; i < cellText.length; i++) {
                    hash = (hash * 31 + cellText.charCodeAt(i)) >>> 0;
                  }
                  const color = palette[hash % palette.length];
                  data.cell.styles.fillColor = color;
                  data.cell.styles.textColor = 255;
                }
                data.cell.styles.fontStyle = "bold";
                data.cell.styles.fontSize = headerFontSize + 1;
              } else if (data.row.index === 1) {
                // Sub-header row (column names)
                data.cell.styles.fillColor = [240, 240, 240]; // light gray
                data.cell.styles.textColor = [0, 0, 0];
                data.cell.styles.fontStyle = "bold";
              }
            }

            // Highlight declared total rows
            if (
              section.rowTypes &&
              section.rowTypes[data.row.index] === "total" &&
              data.section === "body"
            ) {
              data.cell.styles.fontStyle = "bold";
              data.cell.styles.fillColor = [220, 230, 241];
            }

            // Color-code single-level header pivot groups (non-Qualified reports)
            if (
              section.pivotGroups &&
              !section.isQualifiedAttendance &&
              data.section === "head" &&
              data.column.index >= 4
            ) {
              const headerText = String(data.cell.raw || "");
              if (headerText.includes("Badges"))
                data.cell.styles.fillColor = [52, 152, 219];
              else if (headerText.includes("Other Qualifiers"))
                data.cell.styles.fillColor = [46, 204, 113];
              else if (headerText.includes("Breakout Events"))
                data.cell.styles.fillColor = [155, 89, 182];
              else if (headerText.includes("Main Event"))
                data.cell.styles.fillColor = [230, 126, 34];
            }

            // Body cell highlight for Breakout Events (Qualified Attendance)
            if (
              section.cellMeta &&
              data.section === "body" &&
              data.column.index >= 4 &&
              section.isQualifiedAttendance
            ) {
              const meta =
                section.cellMeta[data.row.index]?.[data.column.index - 4];
              if (meta && meta.highlight) {
                if (meta.highlight === "yellow") {
                  data.cell.styles.fillColor = [255, 248, 179]; // light yellow
                } else if (meta.highlight === "green") {
                  data.cell.styles.fillColor = [215, 253, 215]; // light green
                }
              }
            }
          },
        });
      });
      // Open the PDF
      const blob = pdf.output("blob");
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl);
    } catch (err) {
      console.error("PDF generation failed", err);
      alert(`Failed to generate PDF: ${err.message}. See console for details.`);
    } finally {
      setIsLoading(false);
      setIsPreparingPdf(false);
    }
  };

  /*Export report to Excel*/
  const handleSaveReport = (reportType) => {
    if (reportType === "Ticket Purchases") {
      ticketData.forEach((report) => {
        const allTickets = Array.from(
          new Set(
            report.events.flatMap((event) => event.tickets.map((t) => t.ticket))
          )
        );

        const headers = ["Event", ...allTickets, "Total Tickets"];

        const rows = report.events.map((event) => {
          const row = [
            `${event.eventName} - ${event.eventDate}`,
            ...allTickets.map((ticketName) => {
              const t = event.tickets.find((tk) => tk.ticket === ticketName);
              return t ? t.count : 0;
            }),
          ];
          const total = event.tickets.reduce(
            (sum, t) => sum + (t.isAttending ? t.count || 0 : 0),
            0
          );
          return [...row, total];
        });

        const totalCounts = allTickets.map((ticketName) =>
          report.events.reduce((sum, event) => {
            const t = event.tickets.find((tk) => tk.ticket === ticketName);
            return sum + (t ? t.count : 0);
          }, 0)
        );

        const grandTotal = report.events.reduce(
          (sum, event) =>
            sum +
            event.tickets.reduce(
              (inner, t) => inner + (t.isAttending ? t.count || 0 : 0),
              0
            ),
          0
        );
        rows.push(["Total", ...totalCounts, grandTotal]);

        const aoa = [headers, ...rows];
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Report");

        const reportFileName = `Ticket Purchases - ${report.eventType} ${report.year}`;
        XLSX.writeFile(wb, `${reportFileName}.xlsx`);
      });
    } else if (reportType === "Ticket Purchases By Diamond") {
      ticketsByDiamondData.forEach((report) => {
        const allTickets = Array.from(
          new Set(
            report.events.flatMap((event) =>
              event.diamonds.flatMap((diamond) =>
                diamond.tickets.map((t) => t.ticket)
              )
            )
          )
        );

        const headers = [
          "Event Name",
          "Diamond",
          ...allTickets,
          // "Total",
        ];

        const rows = [];

        report.events.forEach((event) => {
          event.diamonds.forEach((diamond) => {
            const row = [
              `${event.eventName} - ${event.eventDate}`,
              diamond.diamond,
              ...allTickets.map((ticketName) => {
                const ticket = diamond.tickets.find(
                  (t) => t.ticket === ticketName
                );
                return ticket ? ticket.count : 0;
              }),
            ];

            // const total = diamond.tickets.reduce(
            //   (sum, t) => sum + (t.isAttending ? t.count || 0 : 0),
            //   0
            // );

            // rows.push([...row, total]);
            rows.push(row);
          });
        });

        const aoa = [headers, ...rows];
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Report");

        const reportName = `Ticket Purchases By Diamond - ${report.eventType} ${report.year}`;
        XLSX.writeFile(wb, `${reportName}.xlsx`);
      });
    } else if (reportType === "Qualified Attendance") {
      qualifiedData.forEach((report) => {
        const iboRows = report.iboData || [];

        const columnOrder = [
          "Badges",
          "Other Qualifiers",
          "Breakout Events",
          "Main Event",
        ];

        const allColumns = columnOrder.flatMap((headerName) => {
          const headerObj = report.headers.find((h) => h.header === headerName);
          return headerObj.columns.map((col) => ({
            header: headerObj.header,
            columnName: col.columnName || col,
          }));
        });

        const headers = [
          "IBO",
          "First Name",
          "Last Name",
          "Pin Level",
          ...allColumns.map((c) => c.columnName),
        ];

        const rows = iboRows.map((ibo) => {
          const base = [ibo.IBO, ibo.firstName, ibo.lastName, ibo.pinLevel];

          const dynamicCols = allColumns.map((c) => {
            const match = ibo.headers?.find(
              (h) => h.columnName === c.columnName
            );
            return match?.count ?? "";
          });

          return [...base, ...dynamicCols];
        });

        const aoa = [headers, ...rows];
        const ws = XLSX.utils.aoa_to_sheet(aoa);

        let colIndex = 4;
        columnOrder.forEach((headerName) => {
          const headerObj = report.headers.find((h) => h.header === headerName);
          if (!headerObj) return;
          if (headerObj.columns.length > 1) {
            if (!ws["!merges"]) ws["!merges"] = [];
            ws["!merges"].push({
              s: { r: 0, c: colIndex },
              e: { r: 0, c: colIndex + headerObj.columns.length - 1 },
            });
          }
          colIndex += headerObj.columns.length;
        });

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Report");
        XLSX.writeFile(wb, `${report.eventName}.xlsx`);
      });
    } else if (reportType === "Qualified Group Attendance") {
      const groups = Array.from(
        new Set(qualifiyingGroupData.map((r) => r.groupName))
      );

      groups.forEach((groupName) => {
        const groupRows = qualifiyingGroupData.filter(
          (r) => r.groupName === groupName
        );

        if (groupRows.length === 0) return;

        const allColumns = Array.from(
          new Set(groupRows.flatMap((r) => r.columns?.map((c) => c.name) || []))
        );

        const headers = [
          "IBO",
          "Last Name",
          "IBOs",
          "PIN",
          "Highest PIN",
          "Email",
          "Topic ID",
          "Diamond",
          "Users",
          ...allColumns,
        ];

        const rows = groupRows.map((item) => {
          const base = [
            item.IBO,
            item.lastName,
            item.displayName,
            item.pin,
            item.hpin,
            item.email,
            item.topicsId,
            item.diamond,
            item.users,
          ];

          const dynamicCols = allColumns.map((colName) => {
            const match = item.columns?.find((c) => c.name === colName);
            return match ? match.value : "";
          });

          return [...base, ...dynamicCols];
        });

        const aoa = [headers, ...rows];
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Report");

        const reportName = groupName;
        XLSX.writeFile(wb, `${reportName}.xlsx`);
      });
    } else if (reportType === "ADA") {
      adaData.forEach((reportObj) => {
        const allRows =
          Array.isArray(reportObj.sortedAdaData) &&
          reportObj.sortedAdaData.length > 0
            ? reportObj.sortedAdaData
            : reportObj.adaData || [];

        if (allRows.length === 0) return;

        const headerMap = (reportObj.headers || []).reduce((acc, h) => {
          acc[h.columnHeaderValue] = h;
          return acc;
        }, {});

        const allColumns = Array.from(
          new Set(
            allRows.flatMap(
              (item) => item.columns?.map((c) => c.columnName) || []
            )
          )
        ).sort((a, b) => {
          const colA = allRows
            .flatMap((r) => r.columns || [])
            .find((c) => c.columnName === a);
          const colB = allRows
            .flatMap((r) => r.columns || [])
            .find((c) => c.columnName === b);

          const sortA = colA?.columnHeaderValue
            ? headerMap[colA.columnHeaderValue]?.columnHeaderSort || 999
            : 999;
          const sortB = colB?.columnHeaderValue
            ? headerMap[colB.columnHeaderValue]?.columnHeaderSort || 999
            : 999;

          return sortA - sortB;
        });

        const headers = [
          "IBO",
          "Last Name",
          "IBOs",
          "PIN",
          "Diamond",
          "Attending Event",
          "ADA Name",
          "Contact",
          ...allColumns,
        ];

        const rows = allRows.map((item) => {
          const base = [
            item.IBO,
            item.lastName,
            item.displayName,
            item.pin,
            item.diamond,
            item.attendingEvent,
            item.adaName,
            item.contact,
          ];

          const dynamicCols = allColumns.map((colName) => {
            const match = item.columns?.find((c) => c.columnName === colName);
            return match ? match.columnValue : "";
          });

          return [...base, ...dynamicCols];
        });

        const aoa = [headers, ...rows];
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Report");

        const reportName = (reportObj.reportTitle || "ADA Report").replace(
          /[/\\?%*:|"<>]/g,
          ""
        );

        XLSX.writeFile(wb, `${reportName}.xlsx`);
      });
    } else if (reportType === "Breakout Sessions") {
      breakoutData.forEach((report) => {
        const rows = report.reports;
        if (!rows || rows.length === 0) return;

        const allColumns = Array.from(
          new Set(
            rows.flatMap(
              (row) =>
                row.columns?.map((c) => `${c.headerName} - ${c.name}`) || []
            )
          )
        );

        const headers = [
          "IBO",
          "Last Name",
          "IBOs",
          "PIN",
          "Email",
          "Topic ID",
          "Diamond",
          "Attending Event",
          "QGroups",
          ...allColumns,
        ];

        const dataRows = rows.map((row) => {
          const base = [
            row.IBO,
            row.lastName,
            row.displayName,
            row.pin,
            row.email,
            row.topicsId,
            row.diamond,
            row.attending,
            row.qGroups?.join(", "),
          ];

          const dynamicCols = allColumns.map((colIdentifier) => {
            const [headerName, name] = colIdentifier.split(" - ");
            const match = row.columns?.find(
              (c) => c.headerName === headerName && c.name === name
            );
            return match ? match.value : "";
          });

          return [...base, ...dynamicCols];
        });

        const aoa = [headers, ...dataRows];
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Report");
        XLSX.writeFile(wb, `${report.title}.xlsx`);
      });
    } else if (reportType === "All Attendees") {
      attendeesData.forEach((report) => {
        const rows = report.reports;
        if (!rows || rows.length === 0) return;

        const grouped = rows.reduce((acc, r) => {
          if (r.columns && typeof r.columns === "object") {
            Object.keys(r.columns).forEach((columnId) => {
              const [groupName, ticketName] = columnId.split("::");
              if (!groupName || !ticketName || ticketName === "Total") return;
              if (!acc[groupName]) acc[groupName] = new Set();
              acc[groupName].add(ticketName);
            });
            return acc;
          }
          if (!r.ticketGroup || !r.ticket) return acc;
          if (!acc[r.ticketGroup]) acc[r.ticketGroup] = new Set();
          acc[r.ticketGroup].add(r.ticket);
          return acc;
        }, {});

        const allColumns = Object.entries(grouped)
          .sort(([a], [b]) => a.localeCompare(b))
          .flatMap(([group, tickets]) => {
            const sortedTickets = Array.from(tickets).sort();
            if (group === "Attending Tickets") {
              return [
                ...sortedTickets.map((t) => `${group} - ${t}`),
                // `${group} - Total`,
              ];
            }
            return sortedTickets.map((t) => `${group} - ${t}`);
          });

        const headers = [
          "IBO",
          "IBOA Last Name",
          "IBOA First Name",
          "IBOB Last Name",
          "IBOB First Name",
          "PIN",
          "Diamond",
          "Attending",
          "Email",
          "Phone",
          "Topics",
          ...allColumns,
        ];

        const dataRows = rows.map((row) => {
          const base = [
            row.IBO,
            row.iboALastName,
            row.iboAFirstName,
            row.iboBLastName,
            row.iboBFirstName,
            row.pin,
            row.diamond,
            row.attending,
            row.email,
            row.phone,
            row.topicsId,
          ];

          let dynamicCols = [];
          Object.entries(grouped)
            .sort(([a], [b]) => a.localeCompare(b))
            .forEach(([group, tickets]) => {
              const usingOrganizedColumns =
                row.columns && typeof row.columns === "object";
              let groupTotal = 0;

              Array.from(tickets)
                .sort()
                .forEach((ticketName) => {
                  const value = usingOrganizedColumns
                    ? Number(row.columns?.[`${group}::${ticketName}`]) || ""
                    : row.ticketGroup === group && row.ticket === ticketName
                    ? row.purchased
                    : "";

                  if (group === "Attending Tickets") {
                    groupTotal += Number(value) || 0;
                  }

                  dynamicCols.push(value);
                });

              if (group === "Attending Tickets") {
                // const attendingTotal = usingOrganizedColumns
                //   ? Number(row.attendingTotal) || 0
                //   : groupTotal;
                // dynamicCols.push(attendingTotal);
              }

              // keeping old duplicate total block commented out too, since it was in the original
              // if (group === "Attending Tickets") {
              //   const attendingTotal = usingOrganizedColumns
              //     ? Number(row.attendingTotal) || 0
              //     : groupTotal;
              //   dynamicCols.push(attendingTotal);
              // }
            });

          return [...base, ...dynamicCols];
        });

        const aoa = [headers, ...dataRows];

        const ws = XLSX.utils.aoa_to_sheet(aoa);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Report");
        XLSX.writeFile(wb, `${report.title}.xlsx`);
      });
    } else if (reportType === "Session Attendance") {
      sessionAttendanceData.forEach((report) => {
        const rows = report.reports;
        if (!rows || rows.length === 0) return;

        const headers = ["IBO", "Last Name", "First Name", "Ticket", "Diamond"];

        const dataRows = rows.map((row) => [
          row.IBO,
          row.lastName,
          row.firstName,
          row.ticket,
          row.diamond,
        ]);

        const aoa = [headers, ...dataRows];

        const ws = XLSX.utils.aoa_to_sheet(aoa);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Report");
        XLSX.writeFile(wb, `${report.title}.xlsx`);
      });
    } else if (reportType === "Session Overview") {
      if (
        !sessionOverviewData ||
        !sessionOverviewData.rows ||
        sessionOverviewData.rows.length === 0
      )
        return;

      const mainTitle = sessionOverviewData.title;
      const headers = ["Session", "Attending", "Capacity", "PCT"];

      const rows = sessionOverviewData.rows.map((session) => [
        session["Session Title"],
        session.Attending,
        session["Room Capacity"],
        session.PCT,
      ]);

      const aoa = [headers, ...rows];
      const ws = XLSX.utils.aoa_to_sheet(aoa);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Report");

      const fileName = mainTitle.replace(/[/\\?%*:|"<>]/g, "");
      XLSX.writeFile(wb, `${fileName}.xlsx`);
    } else if (reportType === "Questionnaire") {
      questionnaireData.forEach((report) => {
        const rows = report.reports || [];
        if (!rows.length) return;

        const byHeader = rows.reduce((acc, r) => {
          if (!acc[r.questionColumnHeader]) {
            acc[r.questionColumnHeader] = new Set();
          }
          acc[r.questionColumnHeader].add(r.questionColumn);
          return acc;
        }, {});

        const groupedHeaders = Object.entries(byHeader).map(
          ([header, questions]) => ({
            headerName: header,
            columns: Array.from(questions),
          })
        );

        const allColumns = groupedHeaders.flatMap((g) => g.columns);

        const headers = ["IBO", "Name", "Attending", ...allColumns];

        const dataRows = rows.map((r) => {
          const base = [r.IBO, r.name, r.attending];

          const dynamicCols = allColumns.map((col) =>
            col === r.questionColumn ? r.responseValue : ""
          );

          return [...base, ...dynamicCols];
        });

        const aoa = [headers, ...dataRows];
        const ws = XLSX.utils.aoa_to_sheet(aoa);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Report");

        const reportName = report.title.replace(/[/\\?%*:|"<>]/g, "");
        XLSX.writeFile(wb, `${reportName}.xlsx`);
      });
    } else if (reportType === "Qualified Group Overview") {
      qualifiedGroupOverviewData.forEach((report) => {
        if (!report) return;

        const sortedColumnGroups = Array.isArray(report.columnGroups)
          ? [...report.columnGroups].sort(
              (a, b) => (a.columnHeaderSort || 0) - (b.columnHeaderSort || 0)
            )
          : [];

        const flattenedColumns = sortedColumnGroups.flatMap((group) =>
          (group.columns || [])
            .slice()
            .sort((a, b) => (a.columnSort || 0) - (b.columnSort || 0))
            .map((column) => ({
              groupName: group.columnHeader,
              columnName: column.columnName,
            }))
        );

        const getNumericValue = (row, groupName, columnName) => {
          const value = row?.values?.[groupName]?.[columnName];
          return Number(value) || 0;
        };

        const getAttendingTotal = (row) => {
          const attendingGroup = sortedColumnGroups.find(
            (group) => group.columnHeader === "Attending"
          );

          if (!attendingGroup) return 0;

          return (attendingGroup.columns || []).reduce((sum, column) => {
            return sum + getNumericValue(row, "Attending", column.columnName);
          }, 0);
        };

        const headers = [
          "Qualified Group",
          ...flattenedColumns.map((col) => col.columnName),
        ];

        const sortedRows = Array.isArray(report.rows)
          ? [...report.rows].sort(
              (a, b) => Number(a?.rowSort || 0) - Number(b?.rowSort || 0)
            )
          : [];

        const rows = sortedRows.map((row) => {
          const dynamicCols = flattenedColumns.map((col) => {
            if (
              col.groupName === "Attending Total" &&
              col.columnName === "Tickets"
            ) {
              return getAttendingTotal(row);
            }

            const value = row?.values?.[col.groupName]?.[col.columnName];

            if (value === null || value === undefined || value === "") {
              return "";
            }

            const numericValue = Number(value);
            return !Number.isNaN(numericValue) && numericValue === 0
              ? "-"
              : value;
          });

          return [row.qualifiedGroup, ...dynamicCols];
        });

        const totalValues = {};
        sortedColumnGroups.forEach((group) => {
          totalValues[group.columnHeader] = {};

          (group.columns || []).forEach((column) => {
            const total = sortedRows.reduce((sum, row) => {
              if (
                group.columnHeader === "Attending Total" &&
                column.columnName === "Tickets"
              ) {
                return sum + getAttendingTotal(row);
              }

              return (
                sum +
                getNumericValue(row, group.columnHeader, column.columnName)
              );
            }, 0);

            totalValues[group.columnHeader][column.columnName] = total;
          });
        });

        // rows.push([
        //   "Total",
        //   ...flattenedColumns.map(
        //     (col) => totalValues[col.groupName]?.[col.columnName] ?? 0
        //   ),
        // ]);

        const aoa = [];

        const majorHeaderRow = ["Qualified Group"];
        sortedColumnGroups.forEach((group) => {
          const sortedColumns = (group.columns || [])
            .slice()
            .sort((a, b) => (a.columnSort || 0) - (b.columnSort || 0));

          majorHeaderRow.push(group.columnHeader);

          for (let i = 1; i < sortedColumns.length; i++) {
            majorHeaderRow.push("");
          }
        });

        aoa.push(majorHeaderRow);
        aoa.push(headers);
        aoa.push(...rows);

        const ws = XLSX.utils.aoa_to_sheet(aoa);

        if (!ws["!merges"]) ws["!merges"] = [];

        ws["!merges"].push({
          s: { r: 0, c: 0 },
          e: { r: 1, c: 0 },
        });

        let colIndex = 1;
        sortedColumnGroups.forEach((group) => {
          const sortedColumns = (group.columns || [])
            .slice()
            .sort((a, b) => (a.columnSort || 0) - (b.columnSort || 0));

          if (sortedColumns.length > 1) {
            ws["!merges"].push({
              s: { r: 0, c: colIndex },
              e: { r: 0, c: colIndex + sortedColumns.length - 1 },
            });
          }

          colIndex += sortedColumns.length;
        });

        ws["!cols"] = [
          { wch: 32 },
          ...flattenedColumns.map(() => ({ wch: 14 })),
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Report");

        const reportName = (
          report.reportTitle || "Qualified Group Overview"
        ).replace(/[/\\?%*:|"<>]/g, "");

        XLSX.writeFile(wb, `${reportName}.xlsx`);
      });
    } else if (reportType === "Platinum Plus") {
      const reports = Array.isArray(platinumPlusData) ? platinumPlusData : [platinumPlusData];
      
      reports.forEach((report) => {
        const selectedColumnIds = report.selectedColumns || [];

        const sortedColumnGroups = Array.isArray(report.columnGroups)
          ? [...report.columnGroups].sort(
              (a, b) => (Number(a.sort) || 0) - (Number(b.sort) || 0)
            )
          : [];

        const filteredColumnGroups = sortedColumnGroups
          .map((group) => {
            const columns = (group.columns || [])
              .slice()
              .sort((a, b) => {
                const sortDiff = (Number(a.sort) || 0) - (Number(b.sort) || 0);
                if (sortDiff !== 0) return sortDiff;
                return (a.name || "").localeCompare(b.name || "");
              })
              .filter((column) =>
                selectedColumnIds.includes(`${group.header}::${column.name}`)
              );

            return {
              ...group,
              columns,
            };
          })
          .filter((group) => group.columns.length > 0);

        const flattenedColumns = filteredColumnGroups.flatMap((group) =>
          group.columns.map((column) => ({
            groupName: group.header,
            columnName: column.name,
          }))
        );

        const identityHeaders = [
          "IBO",
          "Last Name",
          "First Name",
          "Diamond",
          "Pin Level",
          "Users",
          "In-Person",
          "Virtual",
        ];

        const headerRow1 = ["IBO Data", "", "", "", "", "", "", ""];

        filteredColumnGroups.forEach((group) => {
          headerRow1.push(group.header);
          for (let i = 1; i < group.columns.length; i++) {
            headerRow1.push("");
          }
        });

        const headerRow2 = [
          ...identityHeaders,
          ...flattenedColumns.map((col) => col.columnName),
        ];

        const rowsData = Array.isArray(report.rows) ? report.rows : [];

        const getNumericValue = (row, groupName, columnName) =>
          Number(row?.values?.[groupName]?.[columnName] || 0);

        const rows = rowsData.map((row) => [
          row.ibo,
          row.lastName,
          row.firstName,
          row.diamond,
          row.pin,
          row.users,
          row.inPerson,
          row.virtual,
          ...flattenedColumns.map((col) =>
            getNumericValue(row, col.groupName, col.columnName)
          ),
        ]);

        const aoa = [headerRow1, headerRow2, ...rows];
        const ws = XLSX.utils.aoa_to_sheet(aoa);

        ws["!merges"] = ws["!merges"] || [];

        ws["!merges"].push({
          s: { r: 0, c: 0 },
          e: { r: 0, c: 7 },
        });

        let startCol = 8;
        filteredColumnGroups.forEach((group) => {
          ws["!merges"].push({
            s: { r: 0, c: startCol },
            e: { r: 0, c: startCol + group.columns.length - 1 },
          });

          startCol += group.columns.length;
        });

        ws["!cols"] = [
          { wch: 12 },
          { wch: 18 },
          { wch: 18 },
          { wch: 18 },
          { wch: 12 },
          { wch: 10 },
          { wch: 12 },
          { wch: 10 },
          ...flattenedColumns.map(() => ({ wch: 12 })),
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Report");

        const reportName = (report.reportTitle || "Platinum Plus").replace(
          /[/\\?%*:|"<>]/g,
          ""
        );

        XLSX.writeFile(wb, `${reportName}.xlsx`);
      });
    } else if (reportType === "Pacing Report") {
      if (!pacingData) return;

      const { headers, rows, rowTypes } = getPacingExportData(pacingData);
      const dataRows = rows.filter((_, index) => rowTypes[index] !== "total");
      if (dataRows.length === 0) return;

      const aoa = [headers, ...dataRows];
      const ws = XLSX.utils.aoa_to_sheet(aoa);

      ws["!cols"] = [
        { wch: 22 },
        { wch: 30 },
        { wch: 14 },
        { wch: 14 },
        { wch: 14 },
        { wch: 30 },
        { wch: 14 },
        { wch: 14 },
        { wch: 14 },
        { wch: 12 },
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Report");

      const reportName = (pacingData.reportTitle || "Pacing Report").replace(
        /[/\\?%*:|"<>]/g,
        ""
      );

      XLSX.writeFile(wb, `${reportName}.xlsx`);
    } else if (reportType === "Pacing Report with Diamond") {
      if (!Array.isArray(pacingDiamondData)) return;

      pacingDiamondData.forEach((report) => {
        const { headers, rows, rowTypes } = getPacingDiamondExportData(report);
        const dataRows = rows.filter(
          (_, index) => rowTypes[index] !== "total"
        );

        if (dataRows.length === 0) return;

        const aoa = [headers, ...dataRows];
        const ws = XLSX.utils.aoa_to_sheet(aoa);

        ws["!cols"] = [
          { wch: 18 },
          { wch: 34 },
          { wch: 14 },
          { wch: 14 },
          { wch: 14 },
          { wch: 14 },
          { wch: 14 },
          { wch: 14 },
          { wch: 12 },
        ];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Report");

        const reportName = (
          report.reportTitle || "Pacing Report with Diamond"
        ).replace(/[/\\?%*:|"<>]/g, "");

        XLSX.writeFile(wb, `${reportName}.xlsx`);
      });
    }
  };

  const handleSaveReportClick = async (reportType) => {
    setIsSavingReport(true);
    try {
      await Promise.resolve(handleSaveReport(reportType));
    } finally {
      setIsSavingReport(false);
    }
  };

  const handleReportsLengthChange = useCallback((length) => {
    setReportsLength(length);
    setIsGeneratingReport(false);
  }, []);

  const handleClearReport = async () => {
    setIsClearingReport(true);
    try {
      setIsReportGenerated(false);
      setIsReportCardEdited(false);
      setReportCardKey((prevKey) => prevKey + 1);

      setReportSelected({});
      setSelectedFilters([]);
      localStorage.removeItem("selectedFilters");
    } finally {
      setIsClearingReport(false);
    }
  };

  const handleReportCardEdit = () => {
    setIsReportCardEdited(true);
  };

  if (filtersLoading || isLoading) return <Loader />;
  if (filtersError) return <div>Failed to load report data.</div>;
  return (
    <div className={styles.reportsContainer}>
      {isReportCardEdited && (
        <button
          className={styles.clearReportButton}
          onClick={handleClearReport}
          disabled={isClearingReport}
          aria-busy={isClearingReport ? "true" : "false"}
          aria-live="polite"
        >
          <span className={styles.closeIcon}>✖</span>
          {isClearingReport && <span className={styles.spinner} />}
          Clear Report
        </button>
      )}

      <ReportCard
        filterData={filters}
        key={reportCardKey}
        onGenerateReport={handleGenerateReport}
        onGeneratePDF={handleGeneratePDF}
        onSaveReport={handleSaveReportClick}
        onEdit={handleReportCardEdit}
        reportsLength={reportsLength}
        isGeneratingReport={isGeneratingReport}
        isGeneratingPdf={isPreparingPdf}
        isSavingReport={isSavingReport}
        selectedYear={selectedYear}
        setSelectedYear={setSelectedYear}
        initialFilters={selectedFilters}
      />

      {isReportGenerated && (
        <div ref={reportRef}>
          {reportSelected === "Ticket Purchases" && (
            <ReportTableList
              filters={selectedFilters}
              setReportsLength={handleReportsLengthChange}
              setReportData={(data) => setTicketsData(data)}
              fetchTrigger={fetchTrigger}
            />
          )}
          {reportSelected === "Ticket Purchases By Diamond" && (
            <ReportTableListDiamond
              filters={selectedFilters}
              setReportsLength={handleReportsLengthChange}
              setReportData={(data) => setTicketsByDiamondData(data)}
              fetchTrigger={fetchTrigger}
            />
          )}
          {reportSelected === "Qualified Attendance" && (
            <ReportTableListQualified
              filters={selectedFilters}
              setReportsLength={handleReportsLengthChange}
              setReportData={(data) => setQualifiedData(data)}
              fetchTrigger={fetchTrigger}
            />
          )}
          {reportSelected === "Qualified Group Attendance" && (
            <ReportTableListQualifyingGroup
              filters={selectedFilters}
              setReportsLength={handleReportsLengthChange}
              setReportData={handleQualifiyingGroupData}
              fetchTrigger={fetchTrigger}
            />
          )}
          {reportSelected === "Qualified Group Overview" && (
            <ReportTableListQualifiedGroupOverview
              filters={selectedFilters}
              setReportsLength={handleReportsLengthChange}
              setReportData={handleQualifiedOverviewData}
              fetchTrigger={fetchTrigger}
            />
          )}
          {reportSelected === "ADA" && (
            <ReportTableListADA
              filters={selectedFilters}
              setReportsLength={handleReportsLengthChange}
              setReportData={handleAdaData}
              fetchTrigger={fetchTrigger}
            />
          )}
          {reportSelected === "Breakout Sessions" && (
            <ReportTableListBreakout
              filters={selectedFilters}
              setReportsLength={handleReportsLengthChange}
              setReportData={handleBreakoutData}
              fetchTrigger={fetchTrigger}
            />
          )}
          {reportSelected === "All Attendees" && (
            <ReportTableListAllAttendees
              filters={selectedFilters}
              setReportsLength={handleReportsLengthChange}
              setReportData={handleAttendeesData}
              fetchTrigger={fetchTrigger}
            />
          )}
          {reportSelected === "Session Attendance" && (
            <ReportTableListSessionAttendance
              filters={selectedFilters}
              setReportsLength={handleReportsLengthChange}
              setReportData={handleSessionAttendanceData}
              fetchTrigger={fetchTrigger}
            />
          )}
          {reportSelected === "Session Overview" && (
            <ReportTableListSessionOverview
              filters={selectedFilters}
              setReportsLength={handleReportsLengthChange}
              setReportData={(data) => setSessionOverviewData(data)}
              fetchTrigger={fetchTrigger}
            />
          )}
          {reportSelected === "Questionnaire" && (
            <ReportTableListQuestionnaire
              filters={selectedFilters}
              setReportsLength={handleReportsLengthChange}
              setReportData={(data) => handleQuestionnaireData(data)}
              fetchTrigger={fetchTrigger}
            />
          )}
          {reportSelected === "Platinum Plus" && (
            <ReportTableListPlatinumPlus
              filters={selectedFilters}
              setReportsLength={handleReportsLengthChange}
              setReportData={(data) => handlePlatinumPlusData(data)}
              fetchTrigger={fetchTrigger}
            />
          )}
          {reportSelected === "Pacing Report" && (
            <ReportTableListPacing
              filters={selectedFilters}
              setReportsLength={handleReportsLengthChange}
              setReportData={(data) => setPacingData(data)}
              fetchTrigger={fetchTrigger}
            />
          )}
          {reportSelected === "Pacing Report with Diamond" && (
            <ReportTableListPacingDiamond
              filters={selectedFilters}
              setReportsLength={handleReportsLengthChange}
              setReportData={(data) => setPacingDiamondData(data)}
              fetchTrigger={fetchTrigger}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default Reports;
